import { ProviderConsumer as FluentUIThemeConsumer } from '@fluentui/react-northstar';
import React from 'react';

interface ShareBarProps {
  minutes: number;
  totalMinutes: number;
  /** Position in the list, used to pick a tone. */
  rank: number;
  /** For screen readers, since the bar itself carries no text. */
  label: string;
}

/**
 * One agent's proportional share of the ticket total, drawn directly beneath their row.
 *
 * Hand-rolled rather than using the Charts/ stack: that is chart.js on a canvas with
 * legends and tooltips, and its BarHorizontal type is declared but never implemented.
 * Northstar v0 ships no progress bar, so the div-in-div percentage technique from
 * user/UserForm.tsx is the house precedent - but the colours here come from theme tokens
 * rather than the hardcoded hexes that file uses.
 *
 * Tones are assigned by position, not by person, so an agent's colour shifts if the
 * ranking changes. That is deliberate - the bar conveys proportion, not identity.
 */
const ShareBarImpl: React.FC<ShareBarProps> = ({ minutes, totalMinutes, rank, label }) => {
  if (totalMinutes <= 0) return null;
  const pct = Math.round((minutes / totalMinutes) * 100);

  return (
    <FluentUIThemeConsumer
      render={globalTheme => {
        const { colors, colorScheme } = globalTheme.siteVariables;
        // The tokens the real charts use (Charts/ChartTypes/BaseChart.tsx), reordered into
        // two dark-to-light ramps: the three brand stops for the top contributors, then the
        // greys. The chart order interleaved them, so rank 2 came out darker than ranks 0
        // and 1 and the bars read as unranked. Verified to exist - siteVariables is loosely
        // typed, so an invented stop like brand['400'] compiles fine and renders as
        // undefined.
        const tones = [
          colors.brand['800'],
          colors.brand['600'],
          colors.brand['200'],
          colors.grey['500'],
          colors.grey['400'],
          colorScheme.default.borderHover
        ];

        return (
          <div
            role="img"
            aria-label={`${label}: ${pct}%`}
            // No indent of its own - the caller places it in the column beside the
            // avatar, so it lines up under the name and stops where the row's action
            // buttons begin.
            style={{
              height: '6px',
              borderRadius: '3px',
              background: colorScheme.default.background2
            }}
          >
            <div
              style={{
                // Floored at 2% so a tiny contribution is still a visible sliver.
                width: `${Math.max(pct, 2)}%`,
                height: '100%',
                borderRadius: '3px',
                background: tones[Math.min(rank, tones.length - 1)]
              }}
            />
          </div>
        );
      }}
    />
  );
};

export const ShareBar = React.memo(ShareBarImpl);
